/**
 * components/hire/compareDuplicateModal.tsx
 * Side-by-side comparison of a flagged duplicate pair, so the recruiter can see
 * what actually differs before deciding. Replaces the old "Review & merge"
 * behaviour of navigating away to the other profile, which forced you to hold
 * one record in your head while reading the other.
 *
 * Read-only by design: the two write actions available today are "Not a
 * duplicate" (dismiss the flag) and opening either record. There is no v2 merge
 * endpoint yet — see the note in the footer — so this deliberately does not
 * offer a button that cannot do what it says.
 *
 * Rows where the two records disagree are marked, since those are the only rows
 * that matter when choosing which one to keep.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Modal, ActionButton, Icon } from '../ui';
import { showToast } from '../toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { getCandidate } from '../../services/hire/candidateV2';
import type { CandidateDetail } from '../../schemas/hire/candidateV2';

/** One comparison row: a label plus how to read it off a candidate. */
const FIELDS: { label: string; get: (c: CandidateDetail) => string }[] = [
  { label: 'Code', get: (c) => c.code },
  { label: 'Name', get: (c) => c.name },
  { label: 'Email', get: (c) => c.email ?? '—' },
  { label: 'Phone', get: (c) => c.phone ?? '—' },
  { label: 'Status', get: (c) => c.status },
  { label: 'Source', get: (c) => c.source },
  { label: 'Added', get: (c) => new Date(c.createdAt).toLocaleDateString() },
  { label: 'Current role', get: (c) => (c.profile?.titlesHistory ?? [])[0]?.titleSlug ?? '—' },
  { label: 'Location', get: (c) => c.profile?.currentLocation ?? '—' },
  { label: 'Experience', get: (c) => (c.profile?.totalExperienceYears != null ? `${c.profile.totalExperienceYears} y` : '—') },
  { label: 'Notice period', get: (c) => (c.profile?.noticePeriodDays != null ? `${c.profile.noticePeriodDays} days` : '—') },
  { label: 'Skills', get: (c) => {
    const s = c.profile?.skills ?? [];
    return s.length ? `${s.length} · ${s.slice(0, 6).map((x) => x.slug).join(', ')}${s.length > 6 ? '…' : ''}` : '—';
  } },
  { label: 'Resume versions', get: (c) => String(c.resumeVersions?.length ?? 0) },
  { label: 'Consent', get: (c) => c.consentStatus },
  { label: 'Consent expires', get: (c) => (c.consentTtlExpiresAt ? new Date(c.consentTtlExpiresAt).toLocaleDateString() : '—') },
  { label: 'Source document', get: (c) => c.sourceFilename ?? '—' },
];

export default function CompareDuplicateModal({
  open, leftId, rightId, onClose, onOpenCandidate, onDismissFlag, dismissing,
}: {
  open: boolean;
  /** The record the user came from (the flagged repeat). */
  leftId: string | null;
  /** The record it is flagged against (the original). */
  rightId: string | null;
  onClose: () => void;
  onOpenCandidate: (id: string) => void;
  /** "Not a duplicate" — clears the advisory flag on the left record. */
  onDismissFlag: () => void;
  dismissing?: boolean;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, font } = theme;

  const [left, setLeft] = useState<CandidateDetail | null>(null);
  const [right, setRight] = useState<CandidateDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !leftId || !rightId) return;
    let alive = true;
    setLoading(true);
    Promise.all([getCandidate(leftId), getCandidate(rightId)])
      .then(([l, r]) => { if (alive) { setLeft(l); setRight(r); } })
      .catch((e) => { if (alive) showToast('error', 'Could not load the pair', getApiErrorMessage(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, leftId, rightId]);

  const close = () => { setLeft(null); setRight(null); onClose(); };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Compare duplicates"
      size="wide"
      footer={
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <ActionButton label="Close" variant="secondary" onPress={close} />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <ActionButton label="Not a duplicate" variant="secondary" loading={dismissing} onPress={onDismissFlag} />
            {!!right && (
              <ActionButton
                label={`Open ${right.code}`}
                variant="primary"
                icon="arrow-r"
                onPress={() => { const id = right.id; close(); onOpenCandidate(id); }}
              />
            )}
          </View>
        </View>
      }
    >
      {loading || !left || !right ? (
        <View style={{ paddingVertical: 60 }}><ActivityIndicator size="large" color={colors.accent} /></View>
      ) : (
        <View style={{ gap: 12 }}>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, lineHeight: 19 }}>
            {left.duplicateFlagReason || 'These two records look like the same person.'} Rows that differ are marked —
            those are the ones worth checking before you decide which record to keep.
          </Text>

          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, overflow: 'hidden' }}>
            {/* Header: which record is which */}
            <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderBottomColor: colors.line }}>
              <View style={{ width: 132, padding: 10 }} />
              <View style={{ flex: 1, padding: 10, borderLeftWidth: 1, borderLeftColor: colors.line }}>
                <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold }}>THIS RECORD</Text>
                <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold, marginTop: 2 }}>{left.name}</Text>
              </View>
              <View style={{ flex: 1, padding: 10, borderLeftWidth: 1, borderLeftColor: colors.line }}>
                <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold }}>ALREADY ON FILE</Text>
                <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold, marginTop: 2 }}>{right.name}</Text>
              </View>
            </View>

            <ScrollView style={{ maxHeight: 380 }}>
              {FIELDS.map((f, i) => {
                const a = f.get(left);
                const b = f.get(right);
                // 'Code' always differs by definition — not a real disagreement.
                const differs = f.label !== 'Code' && a !== b;
                return (
                  <View
                    key={f.label}
                    style={{
                      flexDirection: 'row',
                      borderBottomWidth: i === FIELDS.length - 1 ? 0 : 1,
                      borderBottomColor: colors.line,
                      backgroundColor: differs ? colors.warnSoft : 'transparent',
                    }}
                  >
                    <View style={{ width: 132, padding: 10, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                      {differs && <Icon name="alert-circle" size={12} color={colors.warn} />}
                      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.medium }}>{f.label}</Text>
                    </View>
                    <View style={{ flex: 1, padding: 10, borderLeftWidth: 1, borderLeftColor: colors.line }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontFamily: f.label === 'Code' ? font.mono : undefined }}>{a}</Text>
                    </View>
                    <View style={{ flex: 1, padding: 10, borderLeftWidth: 1, borderLeftColor: colors.line }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontFamily: f.label === 'Code' ? font.mono : undefined }}>{b}</Text>
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>

          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, lineHeight: 17 }}>
            Nothing is merged automatically. Clear the flag with “Not a duplicate”, or open either record to keep working
            on it — the other one stays exactly as it is.
          </Text>
        </View>
      )}
    </Modal>
  );
}
